/* The saturation/value plane and the hue bar, from `profile-web-mockups-v2/_blocks.css` `.sv` /
 * `.hue` at the `f-zakladki` cut (a 168px plane rather than the 176 the shared block declares).
 *
 * ── The paints are COMPOSED here, out of tokens and two live values ─────────────────────────
 * A picker shows the COLOUR SPACE rather than the theme, so the two washes are theme-independent
 * tokens (`tokens/colors.css`). What this file adds is the half that cannot live there: the hue the
 * trainer is standing on and the handoff's stop list, both set as custom properties by the
 * component — `model/colorPicker` owns the stops, because it measures its interpolation angles off
 * them, and a second copy here would drift the first time one was edited.
 *
 * **The composition has to be in this rule and it is not a shortcut.** A token in `:root` that
 * reads `var(--picker-hue)` is the guaranteed-invalid value there, since a custom property is
 * substituted on the element it is declared on and the hue is set three levels down; that invalid
 * value inherits, and the plane renders blank white with nothing in the console. Measured. So the
 * two `--*-paint` properties below hold the composition, and `background` still reads one token
 * reference, which is what the value allowlist is for.
 *
 * The knob's ring is white with a neutral drop, in both themes: it stands on an arbitrary colour,
 * so a themed ring would disappear against half the space. That is the same reasoning as the phone
 * preview's — see `tokens/colors.css`.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

.picker {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* The plane. `touch-action: none` so a drag is a drag rather than a page scroll on a touch screen,
 * and `crosshair` because the whole box is the control. */
.plane {
  position: relative;
  height: var(--h-account-plane);
  overflow: hidden;
  cursor: crosshair;

  /* The two washes over the live hue. `100% 50%` is the colour space's own fully saturated,
   * mid-lightness form of an angle rather than a palette value — there is nothing to tokenise
   * about the definition of a pure hue. */
  --plane-paint: var(--picker-wash-value), var(--picker-wash-saturation),
    hsl(var(--picker-hue) 100% 50%);

  background: var(--plane-paint);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-picker-plane);
  touch-action: none;
}

.hue {
  position: relative;
  height: var(--h-account-hue-bar);
  margin-top: var(--space-4);
  cursor: pointer;

  --bar-paint: linear-gradient(to right, var(--hue-stops));

  background: var(--bar-paint);
  border-radius: var(--r-pill);
  touch-action: none;
}

.plane:focus-visible,
.hue:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The knob. Positioned by two custom properties the component sets, so nothing about WHERE it is
 * lives in this file — and `pointer-events: none` so a drag that starts on the knob is read by the
 * box under it rather than swallowed. */
.knob {
  position: absolute;
  top: var(--knob-y);
  left: var(--knob-x);
  width: var(--sz-account-knob-sm);
  height: var(--sz-account-knob-sm);
  pointer-events: none;
  background: var(--knob-fill);
  border: var(--border-knob) solid var(--picker-knob-ring);
  border-radius: var(--r-circle);
  box-shadow: var(--shadow-knob);
  transform: translate(-50%, -50%);
}

.hueKnob {
  top: 50%;
}
